import { useProjectParams } from '@hooks/useProjectParams';
import type {
    ITableLikeTreeContext,
    ITableLikeTreeItem,
    ITableLikeTreeItemNotification,
} from 'components/TableLikeTree/context';
import type { ITableLikeTreeFilterConfig } from 'components/TableLikeTree/TableLikeTree';
import { TableLikeTree } from 'components/TableLikeTree/TableLikeTree';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { WorkOrderVersionObjectData } from 'gql/graphql';
import { ContractObjectStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { groupByParentId, sortByParentBaseOrder } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderStructure';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { getFilteredTree } from 'pages/Project/Common/WorkOrderStructure/helpers';
import { useWorkOrderStructureFilters } from 'pages/Project/Common/WorkOrderStructure/hooks/useWorkOrderStructureFilters';
import { Badge, Button, cnMixSpace, IconBackward } from 'uikit';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useWorkOrderVersionObjects } from '../WorkOrderVersion/hooks/useWorkOrderVersionObjects';
import { useWorkOrderVersionSeasons } from '../WorkOrderVersion/hooks/useWorkOrderVersionSeasons';

import { useConstructionTypeVersion } from './hooks/useConstructionTypeVersion';
import { useTitleStructureVersion } from './hooks/useTitleStructureVersion';

import Style from './versionStructure.module.css';

export const WorkOrderVersionStructure = () => {
    const { id: projectId, orderId, versionId, methodology } = useProjectParams();
    const navigate = useNavigate();
    const { projectData } = useProjectData(projectId);
    const workOrderVersionId = Number(versionId);
    const workOrderId = Number(orderId);
    const dispatch = useDispatch();
    const contextRef = useRef<ITableLikeTreeContext | undefined>();

    const [constructionTypeId, setConstructionTypeId] = useState<number>();
    const [seasons] = useWorkOrderVersionSeasons(workOrderVersionId);
    const [workOrderObjects, , , , requestWorkOrderVersionObjects] = useWorkOrderVersionObjects(
        workOrderVersionId,
        false,
    );

    const informers: ITableLikeTreeItemNotification[] = useMemo(
        () =>
            workOrderObjects?.reduce((acc: ITableLikeTreeItemNotification[], item: WorkOrderVersionObjectData) => {
                const itemsToAdd = [
                    ...(item.status === ContractObjectStatusEnum.NewRecord
                        ? [{ icon: <Badge minified size="s" status="success" /> }]
                        : []),
                    ...(item.hasPrices
                        ? [
                              {
                                  message: `В составе ${item?.parentId ? 'подобъекта' : 'объекта'} имеются расценки`,
                                  icon: <Badge size="s" status="system" label="Р" />,
                              },
                          ]
                        : []),
                ];

                if (itemsToAdd.length) {
                    acc.push({
                        items: itemsToAdd,
                        treeItemId: item.id,
                    });
                }

                return acc;
            }, []) || [],
        [workOrderObjects],
    );

    const { constructionTypes } = useConstructionTypeVersion(workOrderVersionId);

    const [uerWorkOrderObjectSelectionListReset] = useResetStoreData({ requestId: 'workOrderVersionObjects' });

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [projectData?.name, projectData?.uniqueId, methodology, projectId],
    );

    const leftFilterConfig: ITableLikeTreeFilterConfig = useMemo(
        () => ({
            filterItems: constructionTypes?.map((ct: { id: number; name: string }) => ({ id: ct.id, title: ct.name })),
            isFictiveFilter: true,
            disabled: true,
            title: 'Виды строительства',
        }),
        [constructionTypes],
    );

    const topFilterConfig: ITableLikeTreeFilterConfig = useMemo(
        () => ({
            filterItems: seasons.map((season: { id: number; year: string }, index: number) => ({
                id: season?.id,
                title: season?.year,
                isFirstActive: index === 0,
                isLastActive: index === seasons.length - 1,
            })),
            title: 'Выбранные сезоны:',
            isReadOnly: true,
            disabled: true,
            isFictiveFilter: false,
        }),
        [seasons],
    );

    const tableTreeItems: ITableLikeTreeItem[] = useMemo(() => {
        if (workOrderObjects?.length) {
            const groupedByParent = groupByParentId(workOrderObjects);

            const mapper = (o: { id: number; name: string; codeWBS?: string | null }): ITableLikeTreeItem => ({
                id: o.id,
                title: o.name,
                codeWBS: o.codeWBS,
                children: sortByParentBaseOrder(groupedByParent[o.id] || [])?.map(mapper),
            });

            return sortByParentBaseOrder(groupedByParent.root || []).map(mapper);
        }

        return [];
    }, [workOrderObjects]);

    const tableListItemsMakeRequest = useCallback(
        (versionWorkOrderId: number, constructionId?: number) => {
            if (!constructionId) {
                return;
            }

            const where = {
                constructionType: { id: { eq: constructionId } },
            };

            uerWorkOrderObjectSelectionListReset(undefined);
            dispatch(requestWorkOrderVersionObjects({ workOrderVersionId: versionWorkOrderId, where }));
        },
        [uerWorkOrderObjectSelectionListReset, dispatch, requestWorkOrderVersionObjects],
    );

    const refreshByConstructionType = useCallback(() => {
        if (workOrderVersionId && constructionTypeId) {
            tableListItemsMakeRequest(workOrderVersionId, constructionTypeId);
        }
    }, [workOrderVersionId, constructionTypeId, tableListItemsMakeRequest]);

    useEffect(() => {
        if (constructionTypes?.length) {
            setConstructionTypeId(constructionTypes[0].id);
        }
    }, [constructionTypes]);

    useEffect(() => {
        refreshByConstructionType();
    }, [refreshByConstructionType]);

    const handleBackVersion = useCallback(() => {
        navigate(`/projects/uer/${projectId}/order/${orderId}/version/${versionId}`);
    }, [navigate, orderId, projectId, versionId]);

    const { titleStructureVersion } = useTitleStructureVersion(workOrderId, workOrderVersionId);

    const gridControls = useMemo(() => {
        const createToggleFn = (state: boolean) => () => contextRef?.current?.switchExpandAll?.(state);

        return (
            <div className={Style.gridControls}>
                <div className={Style.gridControlsLeft}>
                    <Button label="Развернуть все" onClick={createToggleFn(true)} view="clear" size="s" />
                    <Button label="Свернуть все" onClick={createToggleFn(false)} view="clear" size="s" />
                </div>
            </div>
        );
    }, []);

    const { onFiltersUpdated, additionalCols, colFilters, activeFilterValue } = useWorkOrderStructureFilters();

    const filteredItems = useMemo(
        () => getFilteredTree(tableTreeItems, activeFilterValue),
        [activeFilterValue, tableTreeItems],
    );

    return (
        <ProjectPageLayout
            title={titleStructureVersion}
            breadCrumbsItems={breadCrumbsItems}
            style={{ marginBottom: 0 }}
            layoutEntityKey={workOrderVersionId}
            headerBottom={
                <div className="wrapperToolbarLine">
                    <Button
                        className={cnMixSpace({ mR: 'xs' })}
                        label="Вернуться"
                        view="primary"
                        size="s"
                        onClick={handleBackVersion}
                        iconLeft={IconBackward}
                    />
                </div>
            }
        >
            <div className={Style.versionStructureContainer}>
                <TableLikeTree
                    contextRef={contextRef}
                    hierarchyColumnTitle="Объект / подобъект"
                    hierarchyColumnFilterTitle="Объект / подобъект"
                    items={filteredItems}
                    isRowCheckAvailable
                    leftFilterConfig={leftFilterConfig}
                    topFilterConfig={topFilterConfig}
                    areOuterBordersEnabled
                    leftFilterSelectedId={constructionTypeId}
                    isLeftFilterMenuColumnVisible={false}
                    showCheckedItemsCount
                    isDragOperationDisabled
                    isDragButtonHidden
                    onLeftFilterRowSelected={setConstructionTypeId}
                    noMenuColumn
                    isHeaderCheckboxDisabled
                    className="versionStructure"
                    informers={informers}
                    isFilterEnabled
                    onFiltersUpdated={onFiltersUpdated}
                    additionalColumns={additionalCols}
                    filters={colFilters}
                    gridControls={gridControls}
                />
            </div>
        </ProjectPageLayout>
    );
};

WorkOrderVersionStructure.displayName = 'WorkOrderVersionStructure';
